/**
 * The DM's suggested actions: numbered cards, each of which the player can **take or take
 * and change**.
 *
 * Two things landed here together because they are the same control (`../../TODO.md` §6.1,
 * §6.2), and the second is the one that matters:
 *
 * **A choice is an opening, not a commitment.** Clicking one used to submit it and that was
 * the whole interaction, which quietly threw away the only thing an LLM Dungeon Master has
 * over a branching narrative: *"search the collapsed stall"* can become *"search the
 * collapsed stall, but keep my back to the exit"*, and the second sentence is a different
 * turn. So every card carries a second control that writes the choice into the action box
 * instead of sending it — the same `turn/draft.ts` seam the inventory's item rows write
 * through, and the same rule about it: **the affordance offers a sentence, it does not spend
 * a turn.**
 *
 * The fast path is untouched. Clicking the card still submits, because most choices are
 * taken as written and making everyone edit first would be a tax on the common case.
 *
 * **`1`–`6` focus a card; the browser's own `Enter` activates it.** That is the whole
 * keyboard model, and it is deliberately not a selection state of our own: a card is a
 * button, focus is what "selected" means for a button, the focus ring is a rendering of it
 * that already exists and is already correct in every high-contrast mode, and `Enter` on a
 * focused button is a behaviour we do not have to write. Pressing `3` after `2` moves the
 * focus rather than typing a `3` somewhere, because focus never left the document. It also
 * means a keystroke never spends $0.13 on its own — the digit chooses, the `Enter`
 * confirms — which is the same reservation `suggestAction` states for a click the player
 * did not read first.
 *
 * The digit is drawn on the card *and* published as `aria-keyshortcuts`, so the shortcut is
 * visible to the people who look and announced to the people who listen. The badge itself is
 * `aria-hidden`: the card's accessible name has to stay the sentence it will send, or a
 * screen reader offers *"one, keep climbing"* as the thing about to happen.
 *
 * **Neither control is disabled by another story's turn — only submitting is.** Composing
 * through the wait is free; the submit path is the only one that costs money, and the pencil
 * does not touch it.
 *
 * **And the whole shelf folds away.** Six cards in a language that writes long is half a laptop
 * screen, and the story is what the other half is for; `suggestions.ts` argues the preference and
 * why it has to outlive the component. Two things about the control are deliberate. It is a
 * button with `aria-expanded` rather than the panels' `Section` — that is an *uncontrolled*
 * `<details>` which defaults closed and forgets on unmount, and both halves are wrong here — but
 * it borrows `Section`'s vocabulary down to the `+`/`−`, because a disclosure that looks like the
 * two other disclosures in this app is one fewer thing to learn. And **a digit still works while
 * it is folded**: it unfolds the shelf and takes focus to that card, because a shortcut that goes
 * quiet depending on a setting is a shortcut nobody trusts again.
 */
import { useEffect, useRef } from 'react';
import { suggestAction } from '@/features/turn/draft';
import { useSuggestionsStore } from '@/features/turn/suggestions';
import { useT } from '@/i18n/context';
import { isTyping } from '@/ui/typing';

/**
 * `MAX_CHOICES` in `quest/llm/tools.py` is 6, which is also as many number keys as anyone
 * will find without looking. A seventh choice would simply not have a shortcut; the engine
 * refuses to produce one.
 */
const SHORTCUTS = ['1', '2', '3', '4', '5', '6'];

interface ChoicesProps {
  /** `MIN_CHOICES`/`MAX_CHOICES` — 3 to 6. Never empty when this renders. */
  choices: readonly string[];
  /** False while a turn on *another* story holds this client's one turn slot. */
  canSubmit: boolean;
  /** The id of the element saying why, when there is one. */
  describedBy: string | undefined;
  onSubmit: (action: string) => void;
}

export function Choices({ choices, canSubmit, describedBy, onSubmit }: ChoicesProps) {
  const t = useT();
  const collapsed = useSuggestionsStore((state) => state.collapsed);

  /**
   * The cards, in order, so a digit can put focus on one. Indexed by position rather than
   * keyed by the choice text: two identical choices are not something the DM promises never
   * to write, and a map keyed on prose would silently collapse them into one target.
   */
  const cards = useRef<(HTMLButtonElement | null)[]>([]);

  /**
   * The card a digit asked for while the shelf was folded. There is nothing to focus in that
   * frame — the buttons do not exist — so the keystroke leaves its index here and the effect
   * below spends it once React has drawn them.
   */
  const pending = useRef<number | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTyping(event.target)) return;

      const index = SHORTCUTS.indexOf(event.key);
      if (index === -1 || index >= choices.length) return;

      // Only once we know the digit names a choice, so a `4` on a three-choice turn is still
      // whatever the browser would have done with it. Deliberately a bounds check against
      // `choices` rather than against `cards`, which is empty while the shelf is folded and
      // would swallow every shortcut there.
      event.preventDefault();

      const card = cards.current[index];
      if (card) {
        card.focus();
        return;
      }

      pending.current = index;
      useSuggestionsStore.getState().expand();
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [choices.length]);

  useEffect(() => {
    const index = pending.current;
    if (index === null || collapsed) return;
    pending.current = null;
    cards.current[index]?.focus();
  }, [collapsed]);

  return (
    <div className="mb-3">
      {/*
        The count rather than a verb, for the reason `Section` shows one: "6 options" says what
        is behind the disclosure, `aria-expanded` says whether it is open, and between them a
        screen reader gets the whole state without a second string to keep translated. The glyph
        is `aria-hidden` because it is a second rendering of the same fact.
      */}
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-controls={LIST_ID}
        onClick={() => {
          useSuggestionsStore.getState().toggle();
        }}
        className="text-fg-muted hover:text-fg flex w-full cursor-pointer items-center gap-2 py-1.5 text-left font-mono transition-colors"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        <span aria-hidden="true" className="text-fg-faint">
          {collapsed ? '+' : '−'}
        </span>
        <span>{t.plural('ui.turn.choices', choices.length)}</span>
      </button>

      {collapsed ? null : (
        <ul id={LIST_ID} className="flex flex-col gap-1.5" aria-label={t('ui.turn.choices_label')}>
          {choices.map((choice, index) => (
            <li key={`${String(index)}:${choice}`}>
              {/*
                The card is a row rather than a chip in a wrapping cluster, and the number is
                why: digits that wrap mid-line are read as decoration, and a column of them is
                read as a list of options — which is what this is. It also gives the pencil a
                fixed place to live at the end of every row instead of a place that moves with
                the sentence.
              */}
              <div className="border-border hover:border-accent focus-within:border-accent flex items-stretch border transition-colors">
                <span
                  aria-hidden="true"
                  className="text-fg-faint border-border flex w-7 shrink-0 items-center justify-center border-r font-mono tabular-nums select-none"
                  style={{ fontSize: 'var(--text-meta)' }}
                >
                  {SHORTCUTS[index] ?? '·'}
                </span>

                <button
                  type="button"
                  ref={(node) => {
                    cards.current[index] = node;
                  }}
                  disabled={!canSubmit}
                  onClick={() => {
                    onSubmit(choice);
                  }}
                  aria-describedby={describedBy}
                  aria-keyshortcuts={SHORTCUTS[index]}
                  className="text-fg-muted hover:text-accent grow px-3 py-2 text-left font-mono transition-colors disabled:opacity-40 disabled:hover:text-[color:var(--color-fg-muted)]"
                  style={{ fontSize: 'var(--text-meta)' }}
                >
                  {choice}
                </button>

                {/*
                  Never disabled, for the reason the action box is never disabled: this writes
                  a sentence into a text field and nothing else, and the wait for another
                  story's turn is exactly when composing the next one is worth doing.

                  Drawn at `fg-muted` rather than `fg-faint`, and padded to a real target
                  rather than to the glyph: it stretches the row's full height, which is the
                  whole reason it is a cell in a flex row instead of an icon floated into the
                  corner of one. An affordance the player never notices is the same as no
                  affordance, and on a phone there is no hover to reveal it with.
                */}
                <button
                  type="button"
                  onClick={() => {
                    suggestAction(choice);
                  }}
                  aria-label={t('ui.turn.amend', { choice })}
                  className="text-fg-muted hover:text-accent flex shrink-0 items-center px-4 transition-colors"
                >
                  <PencilIcon />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The disclosure's `aria-controls` target. One shelf per screen, so a constant is honest. */
const LIST_ID = 'suggested-actions';

/**
 * The amend affordance, as an inline SVG for the reason `story/ChangeIcon.tsx` argues at
 * length and this one proved again: `✎` was here first and rendered as a hairline a shade off
 * invisible, because a Unicode glyph is drawn by whichever fallback font the reader's OS
 * hands the mono stack — at that font's weight, on that font's baseline. Four path commands
 * on the same 16-unit grid, stroked in `currentColor`, so it takes the button's colour and
 * its hover with it.
 *
 * `aria-hidden`, because the button it sits in already carries the whole sentence as its
 * accessible name.
 */
function PencilIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className="size-3.5 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11.2 2.6 13.4 4.8 5.6 12.6 2.8 13.2 3.4 10.4Z" />
      <path d="M9.8 4 12 6.2" />
    </svg>
  );
}
